/* eslint-disable padding-line-between-statements */
// Import API functions
import { disableMfa, enableMfa, getCurrentUser as getCurrentUserFromApi } from '@qse/api'
import { queryClient } from '@qse/api'
import { type User } from '@qse/constants'
import { triggerAuthChange } from '@qse/realtime'
import { useUserStore } from '@qse/stores'
import { checkAppAccess, forceLogout, storage } from '@qse/utils'
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  updateUserAttributes,
  verifyTOTPSetup,
} from 'aws-amplify/auth'
import { toast } from 'sonner'

import { sessionManager } from './session-manager'
import {
  type AuthOperation,
  AWS_CONFIG_ERROR_MESSAGE,
  createUserFromAttributes,
  getRedirectPath,
  validateAwsConfiguration,
} from './utils'

export interface TOTPSetupResult {
  secretCode: string
  qrCodeUrl: string
}

export interface LoginResult {
  user?: User
  requiresPasswordChange?: boolean
  requiresTOTPSetup?: boolean
  requiresTOTPCode?: boolean
  requiresEmailVerification?: boolean
  session?: any
  totpSetup?: TOTPSetupResult
  isFirstLogin?: boolean
}

export class AuthService {
  private static instance: AuthService
  private userStore = useUserStore.getState()

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }

    return AuthService.instance
  }

  // Validate AWS configuration
  private validateConfig(): boolean {
    return validateAwsConfiguration()
  }

  // Handle user session management
  private async setUserSession(user: User): Promise<void> {
    this.userStore.setUser(user)

    // Check if user has access to current app
    if (!checkAppAccess(user.userRole)) {
      console.log(`Session denied: User role ${user.userRole} not allowed for current app`)
      this.userStore.setAuthenticated(false)
      return
    }

    this.userStore.setAuthenticated(true)

    // Create device-specific session
    await sessionManager.createSession(user.id.toString(), user.email)

    // Store legacy token for backward compatibility
    const tokenData = {
      userId: user.id,
      email: user.email,
      timestamp: Date.now(),
      isValid: true,
    }
    storage.localStorage.setItem('token', JSON.stringify(tokenData))

    // Trigger WebSocket connection after successful authentication
    try {
      triggerAuthChange()
    } catch (error) {
      console.warn('Failed to trigger auth change event:', error)
    }
  }

  // Clear user session
  private clearUserSession(): void {
    this.userStore.reset()
    sessionManager.clearSession()
  }

  // Check if stored token is valid
  // @ts-expect-error - This method is used for backward compatibility
  private isStoredTokenValid(): boolean {
    try {
      const tokenData = storage.localStorage.getItem('token')
      if (!tokenData) return false

      const parsed = JSON.parse(tokenData)
      // Check if token is not older than 24 hours
      const isRecent = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000
      return parsed.isValid && isRecent
    } catch (error) {
      return false
    }
  }

  // Check if user has MFA enabled and their preference
  // @ts-expect-error - check with Osama why this method not used
  private async checkMFAStatus(): Promise<{
    hasTOTP: boolean
    preferred: string | null
    isConfigured: boolean
  }> {
    try {
      const mfaPreference = await fetchMFAPreference()

      const result = {
        hasTOTP: mfaPreference.enabled?.includes('TOTP') || false,
        preferred: mfaPreference.preferred || null,
        isConfigured: !!(mfaPreference.enabled?.length || mfaPreference.preferred),
      }

      console.log('MFA Status Check - Raw preference:', mfaPreference)
      console.log('MFA Status Check - Processed result:', result)

      return result
    } catch (error) {
      console.error('Error checking MFA status:', error)
      return { hasTOTP: false, preferred: null, isConfigured: false }
    }
  }

  // @ts-expect-error - check with Osama why this method not used
  private async markFirstLoginComplete(): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:has_logged_in': 'true',
        },
      })
    } catch (error) {
      console.error('Error marking first login complete:', error)
      // Fallback to localStorage if updating attributes fails
      storage.localStorage.setItem('user_first_login_complete', 'true')
    }
  }

  // Mark user as admin-created (for users created from AWS Dashboard)
  // @ts-expect-error - check with Osama why this method not used
  private async markAsAdminCreated(): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:created_by_admin': 'true',
          'custom:firstName': 'Admin',
          'custom:lastName': 'User',
          'custom:companyName': 'QSE Group',
          'custom:role': 'user',
        },
      })
    } catch (error) {
      console.error('Error marking user as admin-created:', error)
    }
  }

  // Check if user's email is verified
  private async isEmailVerified(): Promise<boolean> {
    try {
      const attributes = await fetchUserAttributes()

      // For users created from AWS Dashboard, consider them verified if they have a user ID
      // This is because admin-created users typically don't need email verification
      const isAdminCreated =
        attributes['custom:created_by_admin'] === 'true' ||
        attributes['custom:has_logged_in'] === 'false'

      // Check if user was created from AWS Dashboard by looking for specific attributes
      // Users created from AWS Dashboard typically don't have custom attributes set initially
      const isLikelyAdminCreated =
        !attributes['custom:firstName'] &&
        !attributes['custom:lastName'] &&
        !attributes['custom:companyName']

      if (isAdminCreated || isLikelyAdminCreated) {
        return true // Consider admin-created users as verified
      }

      return attributes.email_verified === 'true'
    } catch (error) {
      console.error('Error checking email verification:', error)
      return false
    }
  }

  // Setup TOTP for user
  async setupTOTP(): Promise<TOTPSetupResult> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      const totpSetup = await setUpTOTP()
      const currentUser = await getCurrentUser()
      const userEmail = currentUser.signInDetails?.loginId || 'user'
      const appName = 'QSE GROUP'

      const setupUri = totpSetup.getSetupUri(appName)
      const setupUriString = setupUri.toString()
      const secretMatch = setupUriString.match(/secret=([A-Z2-7]+)/i)
      const secretCode = secretMatch ? secretMatch[1] : setupUriString

      const qrCodeUrl = `otpauth://totp/${encodeURIComponent(appName)}:${encodeURIComponent(userEmail)}?secret=${secretCode}&issuer=${encodeURIComponent(appName)}`

      const totpData = {
        secretCode,
        qrCodeUrl,
      }

      console.log('TOTP Setup - Generated data:', {
        userEmail,
        appName,
        setupUriString,
        secretCode,
        qrCodeUrl,
        totpData,
      })

      // Store TOTP setup data temporarily
      storage.localStorage.setItem('totp_setup_data', JSON.stringify(totpData))

      return totpData
    } catch (error) {
      console.error('TOTP Setup - Error:', error)
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  // Verify TOTP setup and enable it
  async verifyTOTP(totpCode: string): Promise<void> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      await verifyTOTPSetup({ code: totpCode })

      // Enable TOTP as preferred MFA method
      await updateMFAPreference({
        totp: 'PREFERRED',
      })

      // Clear temporary TOTP setup data
      storage.localStorage.removeItem('totp_setup_data')

      toast.success('Two-Factor Authentication setup completed successfully!')
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'mfaVerification')
      toast.error(errorMessage)
      throw error
    }
  }

  // Disable TOTP setup (for optional setup)
  async disableTOTPSetup(): Promise<void> {
    try {
      // Clear temporary data
      storage.localStorage.removeItem('totp_setup_data')

      // Mark as disabled for this session
      storage.localStorage.setItem('totp_setup_disabled', 'true')

      toast.info('Two-Factor Authentication disabled. You can enable it later in settings.')
    } catch (error) {
      console.error('Error disabling TOTP setup:', error)
    }
  }

  // Disable TOTP/2FA
  async disableTOTP(totpCode: string): Promise<void> {
    console.log('🚀 ~ AuthService ~ disableTOTP ~ totpCode:', totpCode)
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      // First, let's check if TOTP is actually enabled via API
      try {
        const apiUser = await getCurrentUserFromApi()
        if (apiUser.mfaStatus === null) {
          throw new Error('Two-Factor Authentication is not currently enabled.')
        }
      } catch (apiError) {
        console.error('Error checking MFA status via API:', apiError)
        throw new Error('Two-Factor Authentication is not currently enabled.')
      }

      // For AWS Cognito, we need to verify the TOTP code first
      // We'll use setUpTOTP to create a new setup context, then verifyTOTPSetup
      // This is a workaround since AWS doesn't provide a direct way to verify TOTP without setup context
      try {
        // Create a new TOTP setup context
        await setUpTOTP()
        // Now verify the code
        await verifyTOTPSetup({ code: totpCode })
      } catch (verifyError: any) {
        // If verifyTOTPSetup fails, the code is invalid
        if (
          verifyError.name === 'CodeMismatchException' ||
          verifyError.name === 'NotAuthorizedException' ||
          verifyError.name === 'InvalidParameterException'
        ) {
          throw new Error(
            'Invalid verification code. Please check your authenticator app and try again.',
          )
        }
        throw verifyError
      }

      // Now disable TOTP as MFA method
      await updateMFAPreference({
        totp: 'DISABLED',
      })

      // Update MFA status to 'disabled' via API
      try {
        await disableMfa()
        console.log('2FA disabled - MFA status updated to disabled')
      } catch (apiError) {
        console.error('Error updating MFA status via API:', apiError)
        // Continue even if API update fails
      }

      toast.success('Two-Factor Authentication has been disabled successfully!')
    } catch (error: any) {
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  // Get current 2FA status
  async get2FAStatus(): Promise<{
    isEnabled: boolean
    isConfigured: boolean
    preferred: string | null
  }> {
    try {
      const apiUser = await getCurrentUserFromApi()
      const mfaStatus = apiUser.mfaStatus

      return {
        isEnabled: mfaStatus === 'enabled',
        isConfigured: mfaStatus === 'enabled',
        preferred: mfaStatus === 'enabled' ? 'TOTP' : null,
      }
    } catch (error) {
      console.error('Error getting 2FA status:', error)
      return {
        isEnabled: false,
        isConfigured: false,
        preferred: null,
      }
    }
  }

  // Confirm TOTP code during login
  async confirmTOTPCode(totpCode: string): Promise<User> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      console.log('🔄 2FA - Starting TOTP verification')
      const result = await confirmSignIn({
        challengeResponse: totpCode,
      })

      if (result.isSignedIn) {
        // Wait a bit for the session to be properly updated
        await new Promise(resolve => setTimeout(resolve, 500))

        // Set MFA verification flag for this session BEFORE any API calls
        storage.localStorage.setItem('isMfaVerified', 'true')

        // Ensure MFA status is set to 'enabled' via API
        try {
          await enableMfa()
        } catch (apiError) {
          console.error('Error updating MFA status via API:', apiError)
          // Continue with login even if API update fails
        }
        const currentUser = await getCurrentUserFromApi()
        // Clear temp data AFTER setting MFA verification flag
        this.clearTempData()

        // Set user session
        await this.setUserSession(currentUser as unknown as User)
        return currentUser as unknown as User
      } else {
        throw new Error('Failed to complete sign-in process')
      }
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  // Clear temporary authentication data
  private clearTempData(): void {
    storage.localStorage.removeItem('temp_email')
    storage.localStorage.removeItem('temp_session')
    storage.localStorage.removeItem('totp_setup_data')
    storage.localStorage.removeItem('totp_setup_skipped')
    storage.localStorage.removeItem('user_first_login_complete')
    storage.localStorage.removeItem('isMfaVerified')
    storage.localStorage.removeItem('mfaStepCompleted')
  }

  // Enhanced login with simplified 2FA flow
  async login(
    email: string,
    password: string,
    domain: 'admin' | 'dashboard' = 'dashboard',
  ): Promise<LoginResult> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    return this.attemptLogin(email, password, domain)
  }

  // Separate method to handle login attempts with account switching
  private async attemptLogin(
    email: string,
    password: string,
    domain: 'admin' | 'dashboard' = 'dashboard',
    isRetry: boolean = false,
  ): Promise<LoginResult> {
    try {
      const signInResult = await signIn({
        username: email,
        password,
        options: {
          clientMetadata: {
            domain,
          },
        },
      })

      console.log('🔄 Login - Sign in result:', signInResult)

      // Store temporary data
      storage.localStorage.setItem('temp_email', email)
      storage.localStorage.setItem('temp_session', JSON.stringify(signInResult))

      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return {
          requiresPasswordChange: true,
          session: signInResult,
        }
      }

      // Handle CONFIRM_SIGN_UP (email verification required)
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        return {
          requiresEmailVerification: true,
          session: signInResult,
        }
      }

      // Handle TOTP setup requirement (AWS automatically triggers this)
      if (signInResult.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const totpSetup = await this.setupTOTP()
        return {
          requiresTOTPSetup: true,
          session: signInResult,
          totpSetup,
          isFirstLogin: true,
        }
      }

      // Handle TOTP code requirement (existing 2FA users)
      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return {
          requiresTOTPCode: true,
          session: signInResult,
        }
      }

      // User is signed in successfully
      if (signInResult.isSignedIn) {
        const currentUser = await getCurrentUserFromApi()
        console.log('🔄 Login - Current user:', currentUser)
        // Check if email is verified
        const isEmailVerified = await this.isEmailVerified()
        if (!isEmailVerified) {
          await signOut()
          return {
            requiresEmailVerification: true,
          }
        }

        // Get user data from API to check MFA status
        try {
          // Reuse currentUser data instead of making another API call
          const apiUser = currentUser
          console.log('🔄 Login - API user (reused):', apiUser)
          // Check MFA status from API response
          const mfaStatus = apiUser.mfaStatus

          if (mfaStatus === 'enabled') {
            // User has 2FA enabled, redirect to verification
            console.log('Login - User has 2FA enabled, redirecting to verification')
            return {
              requiresTOTPCode: true,
              session: signInResult,
            }
          } else if (mfaStatus === null) {
            // Only prompt for setup if mfaStatus is null
            console.log('Login - MFA setup required (null status), redirecting to setup')
            const totpSetup = await this.setupTOTP()
            return {
              requiresTOTPSetup: true,
              session: signInResult,
              totpSetup,
              isFirstLogin: false,
            }
          } else {
            // For disabled/skipped, allow direct access
            console.log('Login - MFA is disabled or skipped, proceeding to dashboard')
            // Set MFA verification flag since user skipped/disabled 2FA (no verification needed)
            storage.localStorage.setItem('isMfaVerified', 'true')
            storage.localStorage.setItem('mfaStepCompleted', 'true')
            this.clearTempData()
            return { user: currentUser as unknown as User }
          }
        } catch (apiError) {
          console.error('Error fetching user data from API:', apiError)
          await this.setUserSession(currentUser as unknown as User)
          this.clearTempData()
          return { user: currentUser as unknown as User }
        }
      }
      throw new Error('Unexpected sign-in state')
    } catch (error: any) {
      console.log('🔄 Login - Error:', error)

      // Handle "already signed in" error by attempting account switching
      if (
        error.message &&
        error.message.includes('There is already a signed in user') &&
        !isRetry
      ) {
        console.log('🔄 Login - Attempting account switch by signing out current user')
        try {
          // Sign out current user and retry login
          await signOut()
          // Clear any existing session data
          this.clearTempData()
          // Retry the login
          return this.attemptLogin(email, password, domain, true)
        } catch (signOutError) {
          console.error('🔄 Login - Error during account switch:', signOutError)
          // If sign out fails, fall through to show original error
        }
      }

      const errorMessage = this.getUserFriendlyError(error, 'login')
      toast.error(errorMessage)
      throw error
    }
  }

  // Set new password for users with temporary password
  async setNewPassword(newPassword: string): Promise<LoginResult> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      })

      // After setting new password, check if TOTP setup is required
      if (result.nextStep?.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        const totpSetup = await this.setupTOTP()
        return {
          requiresTOTPSetup: true,
          session: result,
          totpSetup,
          isFirstLogin: true,
        }
      }

      // Check if TOTP code is required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        return {
          requiresTOTPCode: true,
          session: result,
        }
      }

      if (result.isSignedIn) {
        // Get user data from API to check MFA status
        try {
          const apiUser = await getCurrentUserFromApi()

          // Check MFA status from API response
          const mfaStatus = apiUser.mfaStatus

          if (mfaStatus === 'enabled') {
            // User has 2FA enabled, redirect to verification
            return {
              requiresTOTPCode: true,
              session: result,
            }
          } else if (mfaStatus === null) {
            // User doesn't have 2FA set up, redirect to setup

            const totpSetup = await this.setupTOTP()
            return {
              requiresTOTPSetup: true,
              session: result,
              totpSetup,
              isFirstLogin: true,
            }
          } else if (mfaStatus === 'disabled') {
            // User has disabled 2FA, proceed to dashboard

            // Set MFA verification flag since user disabled 2FA (no verification needed)
            storage.localStorage.setItem('isMfaVerified', 'true')

            // Set MFA step completed flag since user has already made their choice
            storage.localStorage.setItem('mfaStepCompleted', 'true')

            // Create user object and complete login
            await this.setUserSession(apiUser as unknown as User)
            this.clearTempData()

            toast.success('Password changed successfully!')
            return { user: apiUser as unknown as User }
          } else {
            // Default case: treat as disabled and offer setup

            const totpSetup = await this.setupTOTP()
            return {
              requiresTOTPSetup: true,
              session: result,
              totpSetup,
              isFirstLogin: true,
            }
          }
        } catch (apiError) {
          console.error('Error fetching user data from API:', apiError)

          // Fallback: create user from Cognito attributes and proceed
          const attributes = await fetchUserAttributes()
          const email = storage.localStorage.getItem('temp_email') || ''
          const user = createUserFromAttributes(
            (await getCurrentUser()).userId,
            attributes as Record<string, string>,
            email,
          )

          await this.setUserSession(user)
          this.clearTempData()

          toast.success('Password changed successfully!')
          return { user }
        }
      } else {
        throw new Error('Failed to complete sign-in process')
      }
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  // Register user with enhanced flow
  async register(registerData: {
    email: string
    password: string
    firstName: string
    lastName: string
    companyName: string
    companyAddress: string
    country: string
    state: string
    city: string
    zipCode: string
    contactPerson: string
    contactEmail: string
    resellerId?: string
    role?: string
    tenantId?: string
  }): Promise<{ requiresEmailVerification: boolean; email: string }> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      const userAttributes: Record<string, string> = {
        email: registerData.email,
        'custom:firstName': registerData.firstName,
        'custom:lastName': registerData.lastName,
        'custom:companyName': registerData.companyName,
        'custom:companyAddress': registerData.companyAddress,
        'custom:country': registerData.country,
        'custom:state': registerData.state,
        'custom:city': registerData.city,
        'custom:zip_code': registerData.zipCode,
        'custom:contactPerson': registerData.contactPerson,
        'custom:contactEmail': registerData.contactEmail,
        'custom:has_logged_in': 'false', // Mark as first-time user
        'custom:created_by_admin': 'false', // Mark as user-created (not admin-created)
      }

      if (registerData.resellerId) {
        userAttributes['custom:resellerId'] = registerData.resellerId
      }
      if (registerData.role) {
        userAttributes['custom:role'] = registerData.role
      } else {
        userAttributes['custom:role'] = 'admin'
      }
      if (registerData.tenantId) {
        userAttributes['custom:tenantId'] = registerData.tenantId
      }

      await signUp({
        username: registerData.email,
        password: registerData.password,
        options: { userAttributes },
      })

      toast.success('Registration successful! Please check your email for verification code.')

      return {
        requiresEmailVerification: true,
        email: registerData.email,
      }
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'register')
      toast.error(errorMessage)
      throw error
    }
  }

  // Confirm registration
  async confirmRegistration(email: string, confirmationCode: string): Promise<void> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      await confirmSignUp({
        username: email,
        confirmationCode,
      })
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'confirm')
      toast.error(errorMessage)
      throw error
    }
  }

  // Complete 2FA setup after verification
  async complete2FASetup(): Promise<User> {
    try {
      const currentUser = await getCurrentUserFromApi()

      // Update MFA status to 'enabled' via API
      try {
        await enableMfa()
        console.log('2FA setup completed - MFA status updated to enabled')
      } catch (apiError) {
        console.error('Error updating MFA status via API:', apiError)
        // Continue with local setup even if API update fails
      }

      // Set MFA verification flag for this session
      storage.localStorage.setItem('isMfaVerified', 'true')

      // Set MFA step completed flag since user went through the setup screen
      storage.localStorage.setItem('mfaStepCompleted', 'true')

      await this.setUserSession(currentUser as unknown as User)
      this.clearTempData()

      toast.success('Setup completed successfully!')
      return currentUser as unknown as User
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  // Disable 2FA setup
  async disable2FASetup(): Promise<User> {
    try {
      const currentUser = await getCurrentUserFromApi()

      // Update MFA status to 'disabled' via API
      try {
        await disableMfa()
      } catch (apiError) {
        console.error('Error updating MFA status via API:', apiError)
        // Continue with local setup even if API update fails
      }

      await this.setUserSession(currentUser as unknown as User)
      this.clearTempData()

      toast.info('Two-Factor Authentication disabled.')
      return currentUser as unknown as User
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  // Reset 2FA skip preference (for settings page)
  resetSkip2FAPreference(): void {
    storage.localStorage.removeItem('totp_setup_permanently_skipped')
    storage.localStorage.removeItem('totp_setup_skipped')
  }

  // Logout user
  async logout(): Promise<void> {
    try {
      await signOut()
    } catch (error) {
      console.error('Cognito logout failed:', error)
      toast.warning('Logged out locally')
    } finally {
      this.clearUserSession()

      // Trigger WebSocket disconnection after logout
      try {
        triggerAuthChange()
      } catch (error) {
        console.warn('Failed to trigger auth change event on logout:', error)
      }
    }
  }

  // Force logout (local only)
  forceLogout(): void {
    this.clearUserSession()
    try {
      queryClient.clear()
    } catch (e) {
      console.warn('Failed to clear React Query cache:', e)
    }
    forceLogout(false)

    // Trigger WebSocket disconnection after force logout
    try {
      triggerAuthChange()
    } catch (error) {
      console.warn('Failed to trigger auth change event on force logout:', error)
    }

    toast.info('You do not have permission to access this app.')
  }

  // Get current user
  async getCurrentUser(): Promise<User | null> {
    try {
      const apiUser = await getCurrentUserFromApi()

      return apiUser as unknown as User
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  }

  // Forgot Password - Send reset code
  async forgotPassword(email: string): Promise<void> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address')
      }

      await resetPassword({
        username: email.toLowerCase().trim(),
      })

      toast.success('Password reset code sent to your email!')
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'forgotPassword')
      toast.error(errorMessage)
      throw error
    }
  }

  // Confirm Reset Password
  async confirmResetPassword(
    email: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<void> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      await confirmResetPassword({
        username: email.toLowerCase().trim(),
        confirmationCode: confirmationCode.trim(),
        newPassword,
      })

      toast.success('Password reset successfully! Please login with your new password.')
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'confirmResetPassword')
      toast.error(errorMessage)
      throw error
    }
  }

  // Update Password (for authenticated users)
  async updatePassword(oldPassword: string, newPassword: string): Promise<LoginResult> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      await updatePassword({
        oldPassword,
        newPassword,
      })

      toast.success('Password updated successfully!')

      // Get current user data
      try {
        const apiUser = await getCurrentUserFromApi()

        // For password updates, all users proceed normally regardless of MFA status
        // since they are already authenticated
        return { user: apiUser as unknown as User }
      } catch (apiError) {
        console.error('Error fetching user data after password update:', apiError)
        // Fallback: return success without user data
        return { user: null as unknown as User }
      }
    } catch (error) {
      const errorMessage = this.getUserFriendlyError(error, 'updatePassword')
      toast.error(errorMessage)
      throw error
    }
  }

  // Get redirect path for operation
  getRedirectPath(operation: AuthOperation = 'login'): string {
    return getRedirectPath(operation)
  }

  // Handle authentication error
  handleError(
    error: any,
    operation:
      | 'login'
      | 'register'
      | 'logout'
      | 'confirm'
      | 'general'
      | 'forgotPassword'
      | 'confirmResetPassword'
      | 'updatePassword'
      | 'mfaVerification',
    setStatus?: (status: string) => void,
  ): void {
    const errorMessage = this.getUserFriendlyError(error, operation)
    setStatus?.(errorMessage)
  }

  // Get user-friendly error message
  public getUserFriendlyError = (
    error: any,
    type:
      | 'login'
      | 'register'
      | 'logout'
      | 'confirm'
      | 'general'
      | 'forgotPassword'
      | 'confirmResetPassword'
      | 'updatePassword'
      | 'mfaVerification' = 'general',
  ): string => {
    console.log('Error details:', { name: error.name, message: error.message, type })

    // Handle operation-specific errors first
    if (type === 'updatePassword') {
      if (error.name === 'NotAuthorizedException') {
        return 'Your current password is incorrect. Please check your current password and try again.'
      }

      if (error.name === 'LimitExceededException') {
        return 'Too many password change attempts. Please wait a few minutes before trying again.'
      }

      if (error.name === 'InvalidPasswordException') {
        return 'Your new password does not meet the security requirements. Please ensure it has at least 8 characters, including one uppercase letter, one lowercase letter, one number, and one special character.'
      }

      if (error.name === 'PasswordValidationException') {
        return 'Your new password does not meet the security requirements. Please choose a stronger password.'
      }

      if (error.name === 'InvalidParameterException') {
        return 'The password change request is invalid. Please check your input and try again.'
      }

      if (error.name === 'ResourceNotFoundException') {
        return 'Unable to update password. Please try logging out and logging back in, then try again.'
      }

      if (error.name === 'TooManyRequestsException') {
        return 'Too many requests. Please wait a moment before trying to change your password again.'
      }

      if (error.name === 'UserNotConfirmedException') {
        return 'Your account is not confirmed. Please contact support for assistance.'
      }

      if (error.name === 'UserNotFoundException') {
        return 'User account not found. Please try logging out and logging back in.'
      }

      // Generic update password error
      if (error.message && error.message.toLowerCase().includes('password')) {
        return `Password update failed: ${error.message}`
      }
    }

    // Handle MFA verification specific errors
    if (type === 'mfaVerification') {
      if (error.name === 'EnableSoftwareTokenMFAException' && error.message === 'Code mismatch') {
        return 'Invalid verification code. Please try again.'
      }

      if (error.name === 'CodeMismatchException') {
        return 'Invalid MFA code. Please try again.'
      }

      if (error.name === 'NotAuthorizedException') {
        return 'Invalid MFA code. Please try again.'
      }

      if (error.name === 'ExpiredCodeException') {
        return 'MFA code has expired. Please try again.'
      }

      if (error.name === 'LimitExceededException') {
        return 'Too many MFA attempts. Please wait before trying again.'
      }

      if (error.name === 'TooManyRequestsException') {
        return 'Too many requests. Please wait before trying again.'
      }

      if (error.name === 'InvalidParameterException') {
        return 'Invalid MFA code format. Please enter a valid 6-digit code.'
      }

      // Generic MFA verification error
      return 'Invalid MFA code. Please try again.'
    }

    // Handle general Cognito errors
    if (error.name === 'UserNotConfirmedException') {
      return 'Your email is not verified. Please check your email for verification code.'
    }

    if (error.name === 'UserLambdaValidationException') {
      // Remove the "PreAuthentication failed with error" prefix if present
      const cleanMessage = error.message.replace('PreAuthentication failed with error', '').trim()

      // Handle specific organization access error
      if (cleanMessage.includes('ORGANIZATION_ACCESS_DISABLED')) {
        return 'Your organization account is disabled. Please contact your admin.'
      }

      // Handle specific organization access suspended error
      if (cleanMessage.includes('ORGANIZATION_ACCESS_SUSPENDED')) {
        return 'Your organization access has been temporarily suspended. Please contact your administrator for assistance.'
      }

      // Handle specific no permission to access app error
      if (cleanMessage.includes('NO_PERMISSION_TO_ACCESS_APP')) {
        return 'You have no permission to access this app.'
      }

      // Handle specific pre login authentication failed error
      if (cleanMessage.includes('PRE_LOGIN_AUTHENTICATION_FAILED')) {
        return 'Something went wrong try again later.'
      }

      // Handle specific user not found error
      if (cleanMessage.includes('INVALID_CREDENTIALS')) {
        return 'User not found. Please check your email address.'
      }

      return cleanMessage || 'User has no access to this app.'
    }

    if (error.name === 'NotAuthorizedException') {
      return 'Invalid email or password. Please try again.'
    }

    if (error.name === 'CodeMismatchException') {
      return 'Invalid verification code. Please check the code and try again.'
    }

    if (error.name === 'ExpiredCodeException') {
      return 'Verification code has expired. Please request a new code.'
    }

    if (error.name === 'LimitExceededException') {
      return 'Too many attempts. Please wait before trying again.'
    }

    if (error.name === 'UserNotFoundException') {
      return 'User not found. Please check your email address.'
    }

    if (error.name === 'TooManyRequestsException') {
      return 'Too many requests. Please wait before trying again.'
    }

    if (error.name === 'InvalidParameterException') {
      return 'Invalid input provided. Please check your information and try again.'
    }

    // Handle specific error messages
    if (error.message && error.message.includes('There is already a signed in user')) {
      return 'Switching accounts... Please wait.'
    }

    if (
      (error.message && error.message.includes('network')) ||
      error.message.includes('connection')
    ) {
      return 'Network connection error. Please check your internet connection and try again.'
    }

    if (error.message && error.message.includes('timeout')) {
      return 'Request timed out. Please try again.'
    }

    // Fallback error message
    return error.message || 'An unexpected error occurred. Please try again.'
  }

  // Resend email verification code
  async resendEmailVerification(email: string): Promise<void> {
    if (!this.validateConfig()) {
      toast.error(AWS_CONFIG_ERROR_MESSAGE)
      throw new Error(AWS_CONFIG_ERROR_MESSAGE)
    }

    try {
      await resendSignUpCode({ username: email })
      toast.success('Verification code sent to your email!')
    } catch (error: any) {
      const errorMessage = this.getUserFriendlyError(error, 'general')
      toast.error(errorMessage)
      throw error
    }
  }

  async checkUserStatus(): Promise<{
    isEmailVerified: boolean
    isAdminCreated: boolean
    isFirstLogin: boolean
    needsEmailVerification: boolean
    needsPasswordChange: boolean
    needsTOTPSetup: boolean
  }> {
    try {
      const attributes = await fetchUserAttributes()
      const isEmailVerified = await this.isEmailVerified()
      const isAdminCreated = attributes['custom:created_by_admin'] === 'true'
      const isFirstLogin = attributes['custom:has_logged_in'] !== 'true'

      // Check if user was created from AWS Dashboard by looking for specific attributes
      // Users created from AWS Dashboard typically don't have custom attributes set initially
      const isLikelyAdminCreated =
        !attributes['custom:firstName'] &&
        !attributes['custom:lastName'] &&
        !attributes['custom:companyName']

      return {
        isEmailVerified,
        isAdminCreated: isAdminCreated || isLikelyAdminCreated,
        isFirstLogin,
        needsEmailVerification: !isEmailVerified && !isAdminCreated && !isLikelyAdminCreated,
        needsPasswordChange: false, // This is determined during login
        needsTOTPSetup: isFirstLogin && !isAdminCreated && !isLikelyAdminCreated,
      }
    } catch (error) {
      console.error('Error checking user status:', error)
      return {
        isEmailVerified: false,
        isAdminCreated: false,
        isFirstLogin: true,
        needsEmailVerification: true,
        needsPasswordChange: false,
        needsTOTPSetup: true,
      }
    }
  }

  // Update admin-created user with proper attributes
  async updateAdminCreatedUser(): Promise<void> {
    try {
      const attributes = await fetchUserAttributes()

      // Only update if user doesn't have custom attributes
      if (!attributes['custom:firstName'] && !attributes['custom:lastName']) {
        await updateUserAttributes({
          userAttributes: {
            'custom:created_by_admin': 'true',
            'custom:firstName': 'Admin',
            'custom:lastName': 'User',
            'custom:companyName': 'QSE Group',
            'custom:role': 'user',
            'custom:has_logged_in': 'true',
          },
        })
      }
    } catch (error) {
      console.error('Error updating admin-created user:', error)
    }
  }

  // Check if user is already authenticated on app startup
  async checkExistingAuth(): Promise<{ isAuthenticated: boolean }> {
    try {
      // Check if there's a valid session in session manager
      const session = sessionManager.getCurrentSession()
      if (!session) {
        this.clearUserSession()
        return { isAuthenticated: false }
      }

      // Update session activity
      sessionManager.updateActivity()

      return {
        isAuthenticated: true,
      }
    } catch (error) {
      console.log('No existing authentication found:', error)
      // Clear any stale session data
      this.clearUserSession()
      return {
        isAuthenticated: false,
      }
    }
  }

  // Check if MFA is verified for current session
  isMfaVerified(): boolean {
    return storage.localStorage.getItem('isMfaVerified') === 'true'
  }

  /**
   * Checks if the MFA setup step has been completed in this session.
   * @returns {boolean} True if MFA setup step is completed, false otherwise.
   */
  isMfaStepCompleted(): boolean {
    return storage.localStorage.getItem('mfaStepCompleted') === 'true'
  }

  // Check if user needs MFA verification
  needsMfaVerification(userMfaStatus: string | null | undefined): boolean {
    return userMfaStatus === 'enabled' && !this.isMfaVerified()
  }

  // Check if user needs to complete MFA setup step
  needsMfaSetup(userMfaStatus: string | null | undefined): boolean {
    // Only require setup if mfaStatus is null (first login or reset)
    return userMfaStatus === null && !this.isMfaStepCompleted()
  }

  // Get session information including duration and expiration
  async getSessionInfo(): Promise<{
    isAuthenticated: boolean
    sessionDuration?: number
    expiresAt?: Date
    remainingTime?: number
    tokenType?: string
    userId?: string
  }> {
    try {
      const session = await fetchAuthSession()

      if (!session.tokens) {
        return { isAuthenticated: false }
      }

      const idToken = session.tokens.idToken
      const accessToken = session.tokens.accessToken

      if (!idToken || !accessToken) {
        return { isAuthenticated: false }
      }

      // Get token expiration times from JWT payload
      const idTokenPayload = idToken.payload
      const accessTokenPayload = accessToken.payload

      // Extract expiration times from JWT payload
      const idTokenExp = idTokenPayload.exp ? new Date(idTokenPayload.exp * 1000) : new Date()
      const accessTokenExp = accessTokenPayload.exp
        ? new Date(accessTokenPayload.exp * 1000)
        : new Date()

      // Use the earlier expiration time
      const expiresAt = new Date(Math.min(idTokenExp.getTime(), accessTokenExp.getTime()))
      const now = new Date()
      const remainingTime = expiresAt.getTime() - now.getTime()

      // Calculate session duration (time since token was issued)
      const issuedAt = idTokenPayload.iat ? new Date(idTokenPayload.iat * 1000) : new Date()
      const sessionDuration = now.getTime() - issuedAt.getTime()

      return {
        isAuthenticated: true,
        sessionDuration,
        expiresAt,
        remainingTime: Math.max(0, remainingTime),
        tokenType: 'JWT',
        userId: idTokenPayload.sub,
      }
    } catch (error) {
      console.error('Error getting session info:', error)
      return { isAuthenticated: false }
    }
  }

  // Check if session is about to expire (within specified minutes)
  async isSessionExpiringSoon(minutes: number = 5): Promise<boolean> {
    try {
      const sessionInfo = await this.getSessionInfo()

      if (!sessionInfo.isAuthenticated || !sessionInfo.remainingTime) {
        return true
      }

      // Check if session expires within the specified minutes
      const minutesInMs = minutes * 60 * 1000
      return sessionInfo.remainingTime <= minutesInMs
    } catch (error) {
      console.error('Error checking session expiration:', error)
      return true
    }
  }

  // Get formatted session duration string
  async getSessionDurationString(): Promise<string> {
    try {
      const sessionInfo = await this.getSessionInfo()

      if (!sessionInfo.isAuthenticated) {
        return 'Not logged in'
      }

      if (!sessionInfo.remainingTime) {
        return 'Session expired'
      }

      const hours = Math.floor(sessionInfo.remainingTime / (1000 * 60 * 60))
      const minutes = Math.floor((sessionInfo.remainingTime % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((sessionInfo.remainingTime % (1000 * 60)) / 1000)

      if (hours > 0) {
        return `${hours} h ${minutes} min`
      } else if (minutes > 0) {
        return `${minutes} min ${seconds} s`
      } else {
        return `${seconds} s`
      }
    } catch (error) {
      console.error('Error getting session duration string:', error)
      return 'Error calculating session duration'
    }
  }

  // Refresh session if needed
  async refreshSessionIfNeeded(): Promise<boolean> {
    try {
      const sessionInfo = await this.getSessionInfo()

      if (!sessionInfo.isAuthenticated) {
        return false
      }

      // If session expires within 10 minutes, try to refresh
      if (sessionInfo.remainingTime && sessionInfo.remainingTime < 10 * 60 * 1000) {
        // AWS Amplify automatically handles token refresh
        // We just need to fetch the session again
        await fetchAuthSession()
        return true
      }

      return true
    } catch (error) {
      console.error('Error refreshing session:', error)
      return false
    }
  }
}

// Export singleton instance
export const authService = AuthService.getInstance()
